import {
  IEmployeeAppInboxResponse,
  IEmployeeAppPinnedItem,
  IEmployeeAppPinnedItemRequestItem,
  IEmployeeAppPinnedItemsRequest,
  IEmployeeAppPinnedItemsResponse,
  TaskStatus
} from '@dadohr/shared/domainTypes/experience';
import { compact } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  LoadingValue,
  useMappedLoadingValue
} from '../../../../../services/firebase';
import { callFirebaseFunction } from '../../../../../services/firebaseFunctions';
import { groupTasks } from '../pages/Inbox/groups';
import { TaskGroup } from '../pages/Inbox/types';

type Mutation =
  | {
      at: string;
      operation: 'ADD';
      items: IEmployeeAppPinnedItemsResponse['items'];
    }
  | {
      at: string;
      operation: 'REMOVE';
      items: IEmployeeAppPinnedItemsRequest['items'];
    };

const getPinnedItems = (userAccessToken: string) =>
  callFirebaseFunction<IEmployeeAppPinnedItemsResponse>(
    'standaloneApp_getPinnedItems',
    { userAccessToken }
  );
const pinItems = (req: IEmployeeAppPinnedItemsRequest) =>
  callFirebaseFunction<IEmployeeAppPinnedItemsResponse>(
    'standaloneApp_pinItems',
    req
  );

const withLocalUpdates = (
  res: IEmployeeAppPinnedItemsResponse,
  localUpdates: Mutation[]
) => {
  return localUpdates.reduce((acc, op) => {
    // TODO
    // First just filter everything out
    // If the operation is an ADD, then re-add it.
    let nextItems: IEmployeeAppPinnedItem[] = [...acc.items];
    const removeItems = (
      items: IEmployeeAppPinnedItem[],
      opItems: {
        type: IEmployeeAppPinnedItem['type'];
        templateId: IEmployeeAppPinnedItem['templateId'];
        itemId: IEmployeeAppPinnedItem['itemId'];
      }[]
    ) => {
      return items.filter((item) => {
        return !opItems.some(
          (i) =>
            item.type === i.type &&
            item.templateId === i.templateId &&
            item.itemId === i.itemId
        );
      });
    };
    if (op.operation === 'ADD') {
      nextItems = removeItems(nextItems, op.items);
      nextItems = [...nextItems, ...op.items];
    }
    if (op.operation === 'REMOVE') {
      nextItems = removeItems(nextItems, op.items);
    }

    return {
      ...acc,
      items: nextItems
    };
  }, res);
};

const createPinnedItemsCache = () => {
  const requestCache: {
    [userAccessToken: string]: Promise<{
      at: string;
      res: IEmployeeAppPinnedItemsResponse;
    }>;
  } = {};

  const localUpdateCache: { [userAccessToken: string]: Mutation[] } = {};

  const mergedCached: {
    [userAccessToken: string]: IEmployeeAppPinnedItemsResponse;
  } = {};

  const getPrunedLocalUpdateCache = (
    userAccessToken: string,
    cutoff: string
  ) => {
    if (!localUpdateCache[userAccessToken]) {
      return [];
    }
    return localUpdateCache[userAccessToken].filter(({ at }) => at > cutoff);
  };

  const listenersByUserAccessToken: {
    [userAccessToken: string]: Set<
      (items: IEmployeeAppPinnedItemsResponse['items']) => void
    >;
  } = {};

  const notify = (res: IEmployeeAppPinnedItemsResponse) => {
    listenersByUserAccessToken[res.userAccessToken]?.forEach((listener) =>
      listener(res.items)
    );
  };

  const getItems = (userAccessToken: string) => {
    if (!requestCache[userAccessToken]) {
      requestCache[userAccessToken] = getPinnedItems(
        userAccessToken
      ).then((res) => ({ at: new Date().toISOString(), res }));
    }
    // This also applies any local updates if necessary
    return requestCache[userAccessToken];
  };

  const getItemsAndMergeAndNotifyAndStore = (userAccessToken: string) => {
    return getItems(userAccessToken)
      .then(({ at, res }) => {
        const localUpdates = getPrunedLocalUpdateCache(userAccessToken, at);
        const merged = withLocalUpdates(res, localUpdates);
        mergedCached[userAccessToken] = merged;
        notify(merged);
      })
      .catch((err) => {
        console.error('Failed to fetch pinned items:', err);
        // Notify with empty items to unblock loading state
        const emptyResponse: IEmployeeAppPinnedItemsResponse = {
          userAccessToken,
          items: []
        };
        mergedCached[userAccessToken] = emptyResponse;
        notify(emptyResponse);
      });
  };

  return {
    subscribe: (
      userAccessToken: string,
      onItems: (items: IEmployeeAppPinnedItemsResponse['items']) => void,
      opts?: {
        skipInitialUpdate?: boolean;
      }
    ) => {
      if (!listenersByUserAccessToken[userAccessToken]) {
        listenersByUserAccessToken[userAccessToken] = new Set();
      }
      listenersByUserAccessToken[userAccessToken].add(onItems);
      if (!opts?.skipInitialUpdate) {
        getItemsAndMergeAndNotifyAndStore(userAccessToken);
      }
      return () => {
        listenersByUserAccessToken[userAccessToken].delete(onItems);
      };
    },
    getItems: (
      userAccessToken: string
    ): IEmployeeAppPinnedItemsResponse['items'] | undefined => {
      return mergedCached[userAccessToken]?.items || undefined;
    },
    mutate: (userAccessToken: string, op: Mutation) => {
      if (!localUpdateCache[userAccessToken]) {
        localUpdateCache[userAccessToken] = [];
      }
      localUpdateCache[userAccessToken].push(op);
      getItemsAndMergeAndNotifyAndStore(userAccessToken);
    }
  };
};

const CACHE = createPinnedItemsCache();

export const usePinnedItems = (
  userAccessToken: string
): LoadingValue<IEmployeeAppPinnedItemsResponse['items']> => {
  const [state, setState] = useState<{
    items: IEmployeeAppPinnedItemsResponse['items'] | void;
    loading: boolean;
    error: any;
  }>({
    items: CACHE.getItems(userAccessToken),
    loading: !CACHE.getItems(userAccessToken),
    error: null
  });
  useEffect(() => {
    return CACHE.subscribe(
      userAccessToken,
      (items) => {
        setState({
          items,
          loading: false,
          error: null
        });
      },
      {
        skipInitialUpdate: !!CACHE.getItems(userAccessToken)
      }
    );
  }, [userAccessToken]);

  return [state.items, state.loading, state.error];
};

export const usePinItemsMutation = (userAccessToken: string) => {
  return useMemo(() => {
    return async (item: IEmployeeAppPinnedItemRequestItem, pinned: boolean) => {
      const requests: (IEmployeeAppPinnedItemRequestItem & {
        pinned: boolean;
      })[] = [{ ...item, pinned }];
      if (pinned) {
        // before the actual request, wait for the response, then publish the mutation
        const res = await pinItems({
          userAccessToken,
          items: requests
        });
        const mutation: Mutation = {
          at: new Date().toISOString(),
          operation: 'ADD',
          items: res.items
        };
        CACHE.mutate(userAccessToken, mutation);
      } else {
        // make the request, there's no need to wait for a response, we can optimistically
        // publish the mutation.
        // But error handling is a bit tricky - we'd need to rollback the mutation if the request fails.
        // Let's ... not bother with this for now. And hope for the best!
        void pinItems({
          userAccessToken,
          items: requests
        });
        const mutation: Mutation = {
          at: new Date().toISOString(),
          operation: 'REMOVE',
          items: requests
        };
        CACHE.mutate(userAccessToken, mutation);
      }
    };
  }, [userAccessToken]);
};

export const useIsItemPinned = (
  userAccessToken: string,
  type: IEmployeeAppPinnedItem['type'],
  templateId: IEmployeeAppPinnedItem['templateId'],
  itemId: IEmployeeAppPinnedItem['itemId']
) => {
  const mapFn = useCallback(
    (items: IEmployeeAppPinnedItemsResponse['items']) => {
      return !!items.find(
        (item) =>
          item.type === type &&
          item.templateId === templateId &&
          item.itemId === itemId
      );
    },
    [type, templateId, itemId]
  );
  return useMappedLoadingValue(usePinnedItems(userAccessToken), mapFn, true);
};

export const usePinnedTaskGroups = (
  data: IEmployeeAppInboxResponse,
  pinnedItems: IEmployeeAppPinnedItemsResponse['items']
) => {
  return useMemo(() => {
    const pinnedTaskKeys = compact(
      pinnedItems.map((item) =>
        item.type === 'TASK' ? `${item.templateId}-${item.itemId}` : null
      )
    );
    const tasks = data.tasks.filter(
      (task) =>
        (task.status === TaskStatus.RUNNING ||
          task.status === TaskStatus.LATE) &&
        pinnedTaskKeys.includes(`${task.templateId}-${task.taskTemplateId}`)
    );
    const defaultGroups: TaskGroup[] = compact(
      pinnedItems.map((item) => {
        if (item.type !== 'TASK') {
          return null;
        }
        return {
          taskTemplateId: item.itemId,
          taskTitle: item.title,
          templateId: item.templateId,
          templateTitle: item.templateTitle,
          tasks: []
        };
      })
    );
    return groupTasks(tasks, defaultGroups);
  }, [data, pinnedItems]);
};
